import { useState, useRef } from 'react'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@creanizer/ui'
import { Button } from '@creanizer/ui'
import { Label } from '@creanizer/ui'
import { Textarea } from '@creanizer/ui'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@creanizer/ui'
import { Alert, AlertDescription } from '@creanizer/ui'
import { Download, Upload, FileDown, FileUp, Package, AlertCircle } from 'lucide-react'
import { useScenarioStore } from '../../../../stores/scenarioStore'
// Toast notifications removed - using console.log for now

export function ImportExportSection() {
  const { 
    scenarios, 
    systems, 
    formats, 
    exportData, 
    importData
  } = useScenarioStore()
  
  const [exportType, setExportType] = useState<'all' | 'scenarios' | 'systems' | 'formats'>('all')
  const [importText, setImportText] = useState('')
  const [importPreview, setImportPreview] = useState<any>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  
  const handleExport = () => {
    let data: any = {}
    if (exportType === 'all') {
      data = JSON.parse(exportData())
    } else if (exportType === 'scenarios') {
      data = { scenarios }
    } else if (exportType === 'systems') {
      data = { systems }
    } else if (exportType === 'formats') {
      data = { formats }
    }
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `creanizer_scenario_${exportType}_${Date.now()}.json`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
    
    const count = exportType === 'all' 
      ? scenarios.length + systems.length + formats.length
      : exportType === 'scenarios' ? scenarios.length
      : exportType === 'systems' ? systems.length
      : formats.length
    
    console.log(`${count}件のデータをエクスポートしました`)
  }
  
  const handleImportPreview = () => {
    try {
      const data = JSON.parse(importText)
      setImportPreview(data)
      
      // Validate data structure
      const counts = {
        scenarios: 0,
        systems: 0,
        formats: 0
      }
      
      if (data.scenarios && Array.isArray(data.scenarios)) {
        counts.scenarios = data.scenarios.length
      }
      if (data.systems && Array.isArray(data.systems)) {
        counts.systems = data.systems.length
      }
      if (data.formats && Array.isArray(data.formats)) {
        counts.formats = data.formats.length
      }
      
      if (counts.scenarios + counts.systems + counts.formats === 0) {
        console.error('有効なデータが見つかりません')
        setImportPreview(null)
      } else {
        console.log(`プレビュー: シナリオ${counts.scenarios}件、システム${counts.systems}件、フォーマット${counts.formats}件`)
      }
    } catch (error) {
      console.error('JSONの解析に失敗しました')
      setImportPreview(null)
    }
  }
  
  const handleImportConfirm = () => {
    if (!importPreview) return
    
    try {
      importData(JSON.stringify(importPreview))
      
      const messages = []
      if (importPreview.scenarios && importPreview.scenarios.length > 0) {
        messages.push(`シナリオ${importPreview.scenarios.length}件`)
      }
      if (importPreview.systems && importPreview.systems.length > 0) {
        messages.push(`システム${importPreview.systems.length}件`)
      }
      if (importPreview.formats && importPreview.formats.length > 0) {
        messages.push(`フォーマット${importPreview.formats.length}件`)
      }
      
      if (messages.length > 0) {
        console.log(`${messages.join('、')}をインポートしました`)
      } else {
        console.error('インポートできるデータがありませんでした')
      }
      
      setImportText('')
      setImportPreview(null)
    } catch (error) {
      console.error('インポートに失敗しました:', error)
    }
  }
  
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return
    
    const reader = new FileReader()
    reader.onload = (e) => {
      const text = e.target?.result as string
      setImportText(text)
      handleImportPreview()
    }
    reader.readAsText(file)
  }
  
  const getExportCount = () => {
    switch (exportType) {
      case 'all':
        return scenarios.length + systems.length + formats.length
      case 'scenarios':
        return scenarios.length
      case 'systems':
        return systems.length
      case 'formats':
        return formats.length
      default:
        return 0
    }
  }
  
  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileDown className="w-5 h-5" />
            エクスポート
          </CardTitle>
          <CardDescription>
            保存されたデータをJSONファイルとしてエクスポートします
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="export-type">エクスポート対象</Label>
            <Select value={exportType} onValueChange={(value: any) => setExportType(value)}>
              <SelectTrigger id="export-type" className="mt-2">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">
                  <div className="flex items-center gap-2">
                    <Package className="w-4 h-4" />
                    すべて（{scenarios.length + systems.length + formats.length}件）
                  </div>
                </SelectItem>
                <SelectItem value="scenarios">
                  シナリオのみ（{scenarios.length}件）
                </SelectItem>
                <SelectItem value="systems">
                  システムのみ（{systems.length}件）
                </SelectItem>
                <SelectItem value="formats">
                  フォーマットのみ（{formats.length}件）
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-500">
              {getExportCount()}件のデータをエクスポートします
            </div>
            <Button 
              onClick={handleExport}
              disabled={getExportCount() === 0}
            >
              <Download className="w-4 h-4 mr-2" />
              エクスポート
            </Button>
          </div>
        </CardContent>
      </Card>
      
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileUp className="w-5 h-5" />
            インポート
          </CardTitle>
          <CardDescription>
            JSONファイルからデータをインポートします
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="import-data">インポートデータ</Label>
            <div className="mt-2 space-y-2">
              <Textarea
                id="import-data"
                value={importText}
                onChange={(e) => setImportText(e.target.value)}
                placeholder="JSONデータを貼り付けるか、ファイルを選択してください"
                rows={10}
                className="font-mono text-sm"
              />
              <div className="flex gap-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".json"
                  onChange={handleFileSelect}
                  className="hidden"
                />
                <Button
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Upload className="w-4 h-4 mr-2" />
                  ファイルを選択
                </Button>
                {importText && (
                  <Button
                    variant="outline"
                    onClick={() => {
                      setImportText('')
                      setImportPreview(null)
                    }}
                  >
                    クリア
                  </Button>
                )}
              </div>
            </div>
          </div>
          
          {importText && !importPreview && (
            <Button onClick={handleImportPreview} className="w-full">
              データを検証
            </Button>
          )}
          
          {importPreview && (
            <>
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  <div className="space-y-1">
                    <p className="font-semibold">インポート内容:</p>
                    {importPreview.scenarios && (
                      <p>• シナリオ: {importPreview.scenarios.length}件</p>
                    )}
                    {importPreview.systems && (
                      <p>• システム: {importPreview.systems.length}件</p>
                    )}
                    {importPreview.formats && (
                      <p>• フォーマット: {importPreview.formats.length}件</p>
                    )}
                  </div>
                </AlertDescription>
              </Alert>
              
              <div className="flex gap-2">
                <Button
                  onClick={handleImportConfirm}
                  className="flex-1"
                >
                  インポートを実行
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setImportPreview(null)
                  }}
                >
                  キャンセル
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
      
      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          <p className="font-semibold mb-2">インポート時の注意事項:</p>
          <ul className="list-disc list-inside space-y-1 text-sm">
            <li>同じIDのデータは上書きされます</li>
            <li>大量のデータをインポートする場合は時間がかかることがあります</li>
            <li>破損したデータはスキップされます</li>
            <li>インポート前に現在のデータをエクスポートしてバックアップすることをお勧めします</li>
          </ul>
        </AlertDescription>
      </Alert>
    </div>
  )
}